/** Non-retrying attempt arms for the standardized `tools/http/fetch` tool: the plain/timeout-raced single attempt and
 * the recursive 401 auth-retry over it. The abort/timeout helpers are injected by the tools composition root (utils
 * leaves may not import each other); the bearer header value is the const-floor `bearerAuth`, read directly.
 *
 * `fetch-attempt-core.ts` opens the same abort controller and races the same pair of promises; the fold across the two
 * was weighed and REFUSED where both are bound, at the `makeSingleAttempt` call in `tools/http/fetch.ts`. Read the
 * grounds there. Peculiar to this arm: it THROWS rather than classifying, and it never touches the controller at all
 * when no `timeoutMs` is supplied -- that path is a bare `fetch` with the caller's own init. */

import { bearerAuth } from '@federa/const/http-bearer-auth';
import { HTTP_STATUS } from '@federa/const/http-status';
import { STANDARD_HEADERS } from '@federa/const/http-header-names';
import type { AuthRetryConfig, HttpAbortTimeoutSeams } from '@federa/types/http';

/** The bound arms: `singleAttempt` throws on failure; `authRetryAttempt` seeds getToken() and refresh-retries on 401. */
interface SingleAttemptApi {
	singleAttempt: (url: string | URL | Request, init: RequestInit | undefined, timeoutMs: number | undefined) => Promise<Response>;
	authRetryAttempt: (opts: {
		url: string | URL | Request;
		init: RequestInit | undefined;
		headers: Headers;
		timeoutMs: number | undefined;
		authRetry: AuthRetryConfig;
		attempt: number;
		maxAttempts: number;
	}) => Promise<Response>;
}

export function makeSingleAttempt(deps: HttpAbortTimeoutSeams): SingleAttemptApi {
	/** One attempt: plain fetch, or timeout-raced when `timeoutMs` is set. Throws on failure. */
	async function singleAttempt(
		url: string | URL | Request,
		init: RequestInit | undefined,
		timeoutMs: number | undefined,
	): Promise<Response> {
		if (timeoutMs === undefined) {
			return fetch(url, init);
		}
		const { signalledInit, timeoutPromise, cleanup } = deps.setupAbortController(init ?? {}, timeoutMs);
		let fetchPromise: Promise<Response> | undefined;
		try {
			fetchPromise = fetch(url, signalledInit);
			return await Promise.race([fetchPromise, timeoutPromise]);
		} catch (error) {
			deps.handleTimeoutError(error, fetchPromise);
			throw error;
		} finally {
			cleanup();
		}
	}

	/** Recursive 401 auth-retry: seed getToken(), on 401 refreshToken() and retry until maxAttempts. */
	async function authRetryAttempt(opts: Parameters<SingleAttemptApi['authRetryAttempt']>[0]): Promise<Response> {
		const { url, init, headers, timeoutMs, authRetry, attempt, maxAttempts } = opts;
		const token = attempt === 0 ? await authRetry.getToken() : await authRetry.refreshToken();
		if (token) {
			headers.set(STANDARD_HEADERS.AUTHORIZATION, bearerAuth(token));
		} else if (attempt > 0) {
			headers.delete(STANDARD_HEADERS.AUTHORIZATION);
		}
		const response = await singleAttempt(url, { ...init, headers }, timeoutMs);
		if (response.status === HTTP_STATUS.UNAUTHORIZED && attempt < maxAttempts - 1) {
			return authRetryAttempt({ ...opts, attempt: attempt + 1 });
		}
		return response;
	}

	return { singleAttempt, authRetryAttempt };
}
